import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
    Box, Checkbox, FormControlLabel, CircularProgress, Alert, Paper
} from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const ContractSigningModal = ({ open, onClose, applicationId, onSigned }) => {
    const { token, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [signing, setSigning] = useState(false);
    const [contractData, setContractData] = useState(null);
    const [signMode, setSignMode] = useState('checkbox');
    const [location, setLocation] = useState(null); 
    const [locationStatus, setLocationStatus] = useState('pending'); 
    
    const [agreed, setAgreed] = useState(false);
    const sigPad = useRef({});

    useEffect(() => {
        if (open && applicationId) {
            setLoading(true);
            
            // 1. Fetch the Contract Template
            api.get(`/applicant/application/${applicationId}/contract-template`, { headers: { Authorization: `Bearer ${token}` } })
               .then(res => {
                   let rawBody = res.data.Body || 'No contract content available.';
                   
                   // CSO Pro-Tip: Dynamically replace the template variables for the demo
                   const finalBody = rawBody
                        .replace(/{{FullName}}/g, user?.fullName || user?.firstNames || 'Applicant')
                        .replace(/{{CurrentDate}}/g, new Date().toLocaleDateString('en-ZA'))
                        .replace(/{{CompanyName}}/g, 'The Sponsoring Trust')
                        .replace(/{{IdNumber}}/g, 'Your ID Number'); // If you have ID in context, use it here

                   // Properly update the actual state variable
                  // The backend has already done the variable replacements!
                   setContractData({ 
                       content: res.data.Body || 'No contract content available.', 
                       status: 'Pending' 
                   });
               })
               .catch(err => {
                   console.error("Contract Fetch Error:", err);
                   setContractData({ content: 'Failed to load contract template. Please contact support.' });
               })
               .finally(() => {
                   // CRITICAL: Tell the UI to stop spinning!
                   setLoading(false);
               });
            
            // 2. Geolocation Tracker (unchanged)
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        setLocation({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude
                        });
                        setLocationStatus('success');
                    },
                    (error) => {
                        console.warn("Location access denied or failed:", error);
                        setLocationStatus('error');
                    }
                );
            } else {
                setLocationStatus('unsupported');
            }
        }
    }, [open, applicationId, token, user]); // Added 'user' to dependency array for the name replacement

    const handleSign = async () => {
        let signatureData = '';

        if (signMode === 'checkbox') {
            if (!agreed) return alert("You must check the box to agree.");
            signatureData = 'AGREED_VIA_CHECKBOX';
        } else {
            if (sigPad.current.isEmpty()) return alert("Please sign in the box.");
            
            // FIX: Use standard toDataURL() instead of getTrimmedCanvas() to avoid webpack issues
            // This gets the full canvas as a transparent PNG, which is perfectly valid
            signatureData = sigPad.current.getCanvas().toDataURL('image/png');
        }

        setSigning(true);
        try {
            await api.post('/contracts/sign', {
                applicationId,
                signatureType: signMode === 'checkbox' ? 'Checkbox' : 'Canvas',
                signatureData,
                contractContent: contractData.content,
                latitude: location?.latitude,   
                longitude: location?.longitude
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            onSigned();
            onClose();
        } catch (e) {
            alert('Signing failed.');
        } finally {
            setSigning(false);
        }
    };

    const clearSig = () => sigPad.current.clear();

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Bursary Contract Agreement</DialogTitle>
            <DialogContent dividers>
                {loading ? <CircularProgress /> : (
                    <>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3, maxHeight: 300, overflow: 'auto', bgcolor: '#fafafa' }}>
                            <Typography 
                                variant="body2" 
                                component="div" 
                                sx={{ 
                                    '& p': { mt: 0, mb: 1.5 }, // Adds nice spacing between paragraphs
                                    '& strong': { fontWeight: 'bold' }
                                }}
                                dangerouslySetInnerHTML={{ __html: contractData?.content || '' }} 
                            />
                        </Paper>

                        {contractData?.status === 'Signed' ? (
                            <Alert severity="success">This contract has already been signed.</Alert>
                        ) : (
                            <Box>
                                <Typography variant="h6" gutterBottom>Sign Agreement</Typography>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Button size="small" variant={signMode === 'checkbox' ? 'contained' : 'outlined'} onClick={() => setSignMode('checkbox')} sx={{ mr: 1 }}>Checkbox</Button>
                                    <Button size="small" variant={signMode === 'canvas' ? 'contained' : 'outlined'} onClick={() => setSignMode('canvas')}>Draw Signature</Button>
                                </Box>

                                {signMode === 'checkbox' ? (
                                    <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
                                            label={`I, ${user?.fullName}, accept the terms of this bursary agreement.`}
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ border: '1px solid #000', width: '100%', height: 200, position: 'relative' }}>
                                        <SignatureCanvas 
                                            ref={sigPad}
                                            penColor='black'
                                            canvasProps={{ width: 500, height: 200, className: 'sigCanvas' }} 
                                        />
                                        <Button size="small" onClick={clearSig} sx={{ position: 'absolute', top: 5, right: 5 }}>Clear</Button>
                                    </Box>
                                )}
                                
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                    <LocationOnIcon fontSize="small" sx={{ mr: 1, color: locationStatus === 'success' ? 'success.main' : 'action.disabled' }} />
                                    <Typography variant="caption">
                                        {locationStatus === 'success' 
                                            ? `Location captured: ${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
                                            : locationStatus === 'pending' ? "Detecting location..." 
                                            : "Location access denied (Signing allowed without location)"}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={signing}>Cancel</Button>
                {contractData?.status !== 'Signed' && (
                    <Button onClick={handleSign} variant="contained" color="primary" disabled={signing || (signMode === 'checkbox' && !agreed)}>
                        {signing ? 'Signing...' : 'Sign & Accept'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ContractSigningModal;